const LOCK_STORAGE_KEY = "kitchen-hub-shared-device-locked";
const SWITCH_TOKEN_STORAGE_KEY = "kitchen-hub-switch-token";

type LockStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function readSharedDeviceLock(storage: LockStorage | null): boolean {
  try {
    return storage?.getItem(LOCK_STORAGE_KEY) === "locked";
  } catch {
    return true;
  }
}

export function writeSharedDeviceLock(storage: LockStorage | null, locked: boolean): void {
  try {
    if (locked) storage?.setItem(LOCK_STORAGE_KEY, "locked");
    else storage?.removeItem(LOCK_STORAGE_KEY);
  } catch {
    // The caller keeps its in-memory state; failed reads remain fail-closed.
  }
}

/**
 * Persists the shared-device switch token across page reloads within its own
 * short lifetime, so refreshing the page after a PIN switch stays capped at
 * AAL1 instead of silently falling back to the underlying named session's
 * (possibly AAL2) bearer token. Without this, an in-memory-only token is lost
 * on every reload and the "switched operator is always AAL1" guarantee
 * (docs/staff-access-security.md) does not survive a refresh.
 */
export function readSwitchToken(storage: LockStorage | null): string | null {
  try {
    const raw = storage?.getItem(SWITCH_TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token: string; expires_at: string };
    if (new Date(parsed.expires_at).getTime() <= Date.now()) {
      storage?.removeItem(SWITCH_TOKEN_STORAGE_KEY);
      return null;
    }
    return parsed.token;
  } catch {
    return null;
  }
}

export function writeSwitchToken(storage: LockStorage | null, token: string | null, expiresAt?: string): void {
  try {
    if (token && expiresAt) storage?.setItem(SWITCH_TOKEN_STORAGE_KEY, JSON.stringify({ token, expires_at: expiresAt }));
    else storage?.removeItem(SWITCH_TOKEN_STORAGE_KEY);
  } catch {
    // The caller keeps its in-memory state; failed reads remain fail-closed.
  }
}
